(() => {
  const variantRequests = new WeakMap();
  let variantRequestId = 0;

  const getTriggers = (gallery) => [...gallery.querySelectorAll('[data-product-gallery-trigger]')];

  const updateGallery = (gallery, mediaId) => {
    gallery.querySelectorAll('[data-product-media-panel]').forEach((panel) => {
      const isActive = panel.dataset.productMediaPanel === mediaId;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
    });

    let activeTrigger;
    getTriggers(gallery).forEach((trigger) => {
      const isActive = trigger.dataset.productGalleryTrigger === mediaId;
      trigger.setAttribute('aria-pressed', String(isActive));
      trigger.classList.toggle('is-active', isActive);
      if (isActive) activeTrigger = trigger;
    });

    const rail = activeTrigger?.closest('.product-gallery__thumbnails');
    if (rail && activeTrigger) {
      const left = activeTrigger.offsetLeft - (rail.clientWidth - activeTrigger.offsetWidth) / 2;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      rail.scrollTo({ left: Math.max(0, left), behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    const status = gallery.querySelector('[data-product-gallery-status]');
    if (status && activeTrigger) status.textContent = activeTrigger.getAttribute('aria-label') || '';
  };

  const showRelativeMedia = (gallery, direction) => {
    const triggers = getTriggers(gallery);
    if (triggers.length < 2) return;

    const activeIndex = triggers.findIndex((trigger) => trigger.getAttribute('aria-pressed') === 'true');
    const nextIndex = (activeIndex + direction + triggers.length) % triggers.length;
    updateGallery(gallery, triggers[nextIndex].dataset.productGalleryTrigger);
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-product-gallery-trigger]');
    const previous = target?.closest('[data-product-gallery-previous]');
    const next = target?.closest('[data-product-gallery-next]');
    const control = trigger || previous || next;
    if (!control) return;

    const gallery = control.closest('[data-product-gallery]');
    if (!gallery) return;

    if (trigger) updateGallery(gallery, trigger.dataset.productGalleryTrigger);
    if (previous) showRelativeMedia(gallery, -1);
    if (next) showRelativeMedia(gallery, 1);
  });

  document.addEventListener('touchstart', (event) => {
    const stage = event.target instanceof Element ? event.target.closest('[data-product-gallery-stage]') : null;
    if (!stage || event.touches.length !== 1) return;
    stage.dataset.swipeStartX = String(event.touches[0].clientX);
    stage.dataset.swipeStartY = String(event.touches[0].clientY);
  }, { passive: true });

  document.addEventListener('touchend', (event) => {
    const stage = event.target instanceof Element ? event.target.closest('[data-product-gallery-stage]') : null;
    if (!stage || event.changedTouches.length !== 1) return;

    const startX = Number(stage.dataset.swipeStartX);
    const startY = Number(stage.dataset.swipeStartY);
    const deltaX = event.changedTouches[0].clientX - startX;
    const deltaY = event.changedTouches[0].clientY - startY;
    delete stage.dataset.swipeStartX;
    delete stage.dataset.swipeStartY;

    if (!Number.isFinite(deltaX) || Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    const gallery = stage.closest('[data-product-gallery]');
    if (gallery) showRelativeMedia(gallery, deltaX < 0 ? 1 : -1);
  }, { passive: true });

  const getSelectedOptionValueIds = (section) => new Set(
    [...section.querySelectorAll('[data-product-option-value]:checked')]
      .map((input) => input.dataset.optionValueId)
      .filter(Boolean)
  );

  const initializeProductSection = (section) => {
    if (!variantRequests.has(section)) {
      variantRequests.set(section, {
        controller: null,
        requestId: 0,
        stableOptionValueIds: getSelectedOptionValueIds(section),
      });
    }
  };

  const initializeProductSections = (scope = document) => {
    if (scope instanceof Element && scope.matches('[data-product-section]')) initializeProductSection(scope);
    scope.querySelectorAll('[data-product-section]').forEach(initializeProductSection);
  };

  document.addEventListener('change', async (event) => {
    const target = event.target instanceof HTMLInputElement ? event.target : null;
    if (!target?.matches('[data-product-option-value]')) return;

    const section = target.closest('[data-product-section]');
    if (!section) return;

    initializeProductSection(section);
    const requestState = variantRequests.get(section);
    requestState.controller?.abort();

    const optionValueIds = [...getSelectedOptionValueIds(section)];

    if (!optionValueIds.length) return;

    const productUrl = new URL(section.dataset.productUrl, window.location.origin);
    productUrl.searchParams.set('option_values', optionValueIds.join(','));
    productUrl.searchParams.delete('variant');

    const sectionUrl = new URL(productUrl);
    sectionUrl.searchParams.set('section_id', section.dataset.productSectionId);
    const requestController = new AbortController();
    const requestId = ++variantRequestId;
    requestState.controller = requestController;
    requestState.requestId = requestId;
    section.dataset.loading = 'true';

    try {
      const response = await fetch(sectionUrl, {
        signal: requestController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error('Product section request failed');

      const markup = await response.text();
      if (requestState.requestId !== requestId) return;

      const nextSection = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-product-section]');
      if (!nextSection) throw new Error('Product section response was incomplete');

      variantRequests.set(nextSection, {
        controller: null,
        requestId: 0,
        stableOptionValueIds: getSelectedOptionValueIds(nextSection),
      });
      section.replaceWith(nextSection);
      window.history.replaceState({}, '', `${productUrl.pathname}${productUrl.search}${productUrl.hash}`);
      const focusTarget = nextSection.querySelector(`#${CSS.escape(target.id)}`)
        || nextSection.querySelector('[data-product-option-value]:checked');
      focusTarget?.focus({ preventScroll: true });
    } catch (error) {
      if (requestState.requestId !== requestId) return;
      section.querySelectorAll('[data-product-option-value]').forEach((input) => {
        input.checked = requestState.stableOptionValueIds.has(input.dataset.optionValueId);
      });
    } finally {
      if (requestState.requestId === requestId && section.isConnected) section.removeAttribute('data-loading');
    }
  });

  const loadRecommendations = (root) => {
    if (!root?.dataset.url || root.dataset.loading === 'true' || root.dataset.loaded === 'true') return;
    root.dataset.loading = 'true';

    fetch(root.dataset.url)
      .then((response) => {
        if (!response.ok) throw new Error('Product recommendations request failed');
        return response.text();
      })
      .then((markup) => {
        const nextRoot = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-product-recommendations]');
        if (nextRoot?.innerHTML.trim()) {
          root.innerHTML = nextRoot.innerHTML;
          root.hidden = false;
          [...root.classList]
            .filter((className) => className.startsWith('product-recommendations--count-'))
            .forEach((className) => root.classList.remove(className));
          [...nextRoot.classList]
            .filter((className) => className.startsWith('product-recommendations--count-'))
            .forEach((className) => root.classList.add(className));
          root.dataset.productCount = nextRoot.dataset.productCount;
          root.dataset.loaded = 'true';
          root.removeAttribute('data-loading');
        } else {
          root.hidden = true;
          root.dataset.loaded = 'true';
          root.removeAttribute('data-loading');
        }
      })
      .catch(() => {
        root.removeAttribute('data-loading');
      });
  };

  const initializeRecommendations = (scope = document) => {
    scope.querySelectorAll('[data-product-recommendations]').forEach(loadRecommendations);
  };

  initializeProductSections();
  initializeRecommendations();
  document.addEventListener('shopify:section:load', (event) => {
    initializeProductSections(event.target);
    initializeRecommendations(event.target);
  });
})();
